import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { DEFAULT_PINNED_KEYS, NAV_FUNCTIONS } from '../lib/navFunctions';

const ALL_KEYS = NAV_FUNCTIONS.map((f) => f.key);

const NavConfigContext = createContext(null);

export const NavConfigProvider = ({ children }) => {
  const [pinned, setPinned] = useState(DEFAULT_PINNED_KEYS);
  const [loading, setLoading] = useState(true);

  const normalize = useCallback((list) => {
    if (!Array.isArray(list)) return DEFAULT_PINNED_KEYS;
    // Keep only known keys, preserving catalog order for stable rendering.
    const set = new Set(list);
    return ALL_KEYS.filter((k) => set.has(k));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/nav-config');
      setPinned(data?.sidebar_functions == null ? DEFAULT_PINNED_KEYS : normalize(data.sidebar_functions));
    } catch (e) {
      setPinned(DEFAULT_PINNED_KEYS);
    } finally {
      setLoading(false);
    }
  }, [normalize]);

  useEffect(() => { load(); }, [load]);

  // Persist a new pinned list (admin only on the backend). Optimistic update.
  const savePinned = useCallback(async (nextList) => {
    const next = normalize(nextList);
    const prev = pinned;
    setPinned(next);
    try {
      await api.put('/nav-config', { sidebar_functions: next });
      return true;
    } catch (e) {
      setPinned(prev); // revert on failure
      throw e;
    }
  }, [pinned, normalize]);

  const isPinned = useCallback((key) => pinned.includes(key), [pinned]);

  const pinFunction = useCallback((key) => savePinned([...pinned, key]), [pinned, savePinned]);
  const unpinFunction = useCallback((key) => savePinned(pinned.filter((k) => k !== key)), [pinned, savePinned]);

  return (
    <NavConfigContext.Provider value={{ pinned, isPinned, pinFunction, unpinFunction, savePinned, reload: load, loading }}>
      {children}
    </NavConfigContext.Provider>
  );
};

export const useNavConfig = () => {
  const ctx = useContext(NavConfigContext);
  if (!ctx) {
    // Safe fallback if used outside the provider.
    return {
      pinned: DEFAULT_PINNED_KEYS, isPinned: () => true,
      pinFunction: async () => {}, unpinFunction: async () => {},
      savePinned: async () => {}, reload: async () => {}, loading: false,
    };
  }
  return ctx;
};
